import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URI } from "../../constants/GlobalConstants";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  Badge,
} from "@mui/material";
import { 
  VisibilityOutlined,
  AddCircleOutline,
  CalculateOutlined,
  FunctionsOutlined,
  AccountTreeOutlined,
  InventoryOutlined,
  RefreshOutlined,
  SearchOutlined,
} from "@mui/icons-material";

const AllCalculationsTable = () => {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCalculations = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_URI}/api/inventory/calculations/all`,
          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`,
            },
          }
        );
        setCalculations(response.data.data);
      } catch (err) {
        setError("Failed to fetch calculations");
      } finally {
        setLoading(false);
      }
    };

    fetchCalculations();
  }, [userInfo.token]);

  const handleViewClick = (id) => {
    navigate(`/erp/settings/calculation/${id}`);
  };

  const handleAddCalculation = () => {
    navigate("/erp/settings/production-calculation");
  };

  const handleRefresh = () => {
    setError("");
    const fetchCalculations = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_URI}/api/inventory/calculations/all`,
          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`,
            },
          }
        );
        setCalculations(response.data.data);
      } catch (err) {
        setError("Failed to fetch calculations");
      } finally {
        setLoading(false);
      }
    };
    fetchCalculations();
  };

  const getFormulaTypeColor = (operand) => {
    const colors = {
      'addition': 'success',
      'subtraction': 'warning',
      'multiplication': 'info',
      'division': 'error',
      'percentage': 'secondary',
    };
    return colors[operand?.toLowerCase()] || 'default';
  };

  if (loading) {
    return (
      <Box p={4}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh">
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="white">
            Loading calculations...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={4}>
      {/* Header Section */}
      <Card elevation={3} sx={{ mb: 3, background: 'linear-gradient(135deg, #091650ff 0%, #060750ff 100%)' }}>
        <CardContent>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={8}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CalculateOutlined sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Production Calculations
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Manage and monitor all inventory calculation formulas
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} mt={{ xs: 2, md: 0 }}>
                <Tooltip title="Refresh Data">
                  <IconButton 
                    onClick={handleRefresh} 
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                    }}
                  >
                    <RefreshOutlined />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutline />}
                  onClick={handleAddCalculation}
                  sx={{
                    backgroundColor: 'white',
                    color: '#667eea',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                    }
                  }}
                >
                  Add Calculation
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                }}>
                  <InventoryOutlined />
                </Box>
                <Box>
                  <Typography variant="h6" component="div">
                    {calculations.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Calculations
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  backgroundColor: 'success.light',
                  color: 'success.contrastText'
                }}>
                  <FunctionsOutlined />
                </Box>
                <Box>
                  <Typography variant="h6" component="div">
                    {calculations.reduce((acc, calc) => acc + calc.formulas.length, 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Formulas
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  backgroundColor: 'warning.light',
                  color: 'warning.contrastText'
                }}>
                  <AccountTreeOutlined />
                </Box>
                <Box>
                  <Typography variant="h6" component="div">
                    {new Set(calculations.flatMap(calc => calc.dependencies)).size}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique Dependencies
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  backgroundColor: 'info.light',
                  color: 'info.contrastText'
                }}>
                  <SearchOutlined />
                </Box>
                <Box>
                  <Typography variant="h6" component="div">
                    {calculations.filter(calc => calc.dependencies.length > 0).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    With Dependencies
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Main Table */}
      <Card elevation={3}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, pb: 1 }}>
            <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalculateOutlined color="primary" />
              Calculation Details
            </Typography>
            <Divider sx={{ mt: 2 }} />
          </Box>
          
          {calculations.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={6}>
              <CalculateOutlined sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No calculations found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Get started by creating your first calculation
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                onClick={handleAddCalculation}
                size="large"
              >
                Add First Calculation
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <InventoryOutlined fontSize="small" />
                        <span>Inventory Name</span>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <FunctionsOutlined fontSize="small" />
                        <span>Formulas</span>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <AccountTreeOutlined fontSize="small" />
                        <span>Dependencies</span>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calculations.map((calc, calcIndex) => (
                    <TableRow 
                      key={calc._id}
                      hover
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.25' } }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {calc.inventoryName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {calc._id.slice(-6)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          {calc.formulas.map((formula, index) => (
                            <Card key={index} variant="outlined" sx={{ p: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                                <Chip 
                                  label={formula.key} 
                                  variant="filled" 
                                  size="small"
                                  color="primary"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                                  {formula.expression}
                                </Typography>
                                <Chip 
                                  label={formula.operand} 
                                  variant="outlined" 
                                  size="small"
                                  color={getFormulaTypeColor(formula.operand)}
                                />
                              </Stack>
                            </Card>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {calc.dependencies.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              No dependencies
                            </Typography>
                          ) : (
                            calc.dependencies.map((dep, index) => (
                              <Chip 
                                key={index}
                                label={dep} 
                                variant="outlined" 
                                size="small"
                                color="secondary"
                              />
                            ))
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Calculation Details">
                          <IconButton
                            color="primary"
                            onClick={() => handleViewClick(calc._id)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText'
                              }
                            }}
                          >
                            <VisibilityOutlined />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AllCalculationsTable;